import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import appStore from "../storage/AppStore";
import systemStore from "../storage/SystemStore";
import clubStore from "../storage/ClubStore";

import Btn from "../ui/Btn";
import Select from "../ui/Select";

import "../common/game.css";

const safeArray = (v) => (Array.isArray(v) ? v : []);

const CompanySearch = ({
  sport_id,
  excludeIds = [],
  onSelect,
  title = null,
}) => {
  const [name, setName] = useState("");
  const [country_id, setCountryId] = useState(null);
  const [region_id, setRegionId] = useState(null);
  const [district_id, setDistrictId] = useState(null);

  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [companies, setCompanies] = useState([]);

  const excludedSet = useMemo(
    () => new Set((excludeIds || []).map((x) => Number(x))),
    [excludeIds]
  );

  useEffect(() => {
    clubStore.companySearch?.getCountries?.()
      .then((ret) => setCountries(safeArray(ret?.countries || ret || [])))
      .catch(console.error);
  }, []);

  useEffect(() => {
    setRegionId(null);
    setDistrictId(null);
    setRegions([]);
    setDistricts([]);

    if (!country_id) return;

    clubStore.companySearch?.getRegions?.(country_id)
      .then((ret) => setRegions(safeArray(ret?.items || ret || [])))
      .catch(console.error);
  }, [country_id]);

  useEffect(() => {
    setDistrictId(null);
    setDistricts([]);

    if (!region_id) return;

    clubStore.companySearch?.getDistricts?.(region_id)
      .then((ret) => setDistricts(safeArray(ret?.items || ret || [])))
      .catch(console.error);
  }, [region_id]);

  const handleSearch = async () => {
    try {
      const ret = await clubStore.companySearch?.searchCompanies?.({
        name,
        sport_id,
        country_id,
        region_id,
        district_id,
      });

      const items = safeArray(ret?.companies || ret || []).filter(
        (x) => x?.id && !excludedSet.has(Number(x.id))
      );

      setCompanies(items);
    } catch (err) {
      console.error(err);
      systemStore.setError(err?.message || "Search failed");
    }
  };

  const handleClear = async () => {
    setName("");
    setCountryId(null);
    setRegionId(null);
    setDistrictId(null);
    setRegions([]);
    setDistricts([]);
    setCompanies([]);
  };

  const renderCompanyCard = (item) => {
    const country = item.country_name;
    const region = item.region_name;
    const district = item.district_name;

    return (
      <div key={item.id} className="game-card">
        <div className="game-card-clickable" role="presentation" tabIndex={-1}>
          <div className="game-card-top">
            <div className="game-meta">
              <div className="game-date">{item.name || "-"}</div>

              <div className="game-address">
                {[country, region, district].filter(Boolean).join(", ")}
              </div>
            </div>
          </div>

          <div className="game-card-bottom">
            <div className="game-badges">
              {sport_id && (
                <span className="badge badge-tournament">
                  {appStore.sports.find(s => s.id === sport_id)?.name}
                </span>
              )}

              {country && (
                <span className="badge badge-upcoming">
                  🌍 {country}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="game-card-actions">
          <Btn
            icon="pi pi-plus"
            severity="success"
            label={appStore.tr.send_invitation || "Select"}
            onClick={() => onSelect?.(item)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="col-12">
      <div className="game-page mb-4">
        <div className="game-card-clickable" role="presentation" tabIndex={-1}>
          <div className="game-card-top">
            <div className="game-meta">
              <div className="game-date">
                {title || appStore.tr.company_search || "Company Search"}
              </div>

              <div className="game-address">
                {sport_id
                  ? `${appStore.tr.sport || "Sport"}: ${appStore.sports.find(s => s.id === sport_id)?.name}`
                  : (appStore.tr.select_filters || "Select filters")}
              </div>
            </div>
          </div>

          <div className="p-3">
            <div className="grid">

              <div className="col-12 md:col-4">
                <Select
                  options={countries}
                  value={country_id}
                  onChange={(e) => {
                    setCountryId(e)
                  }}
                  optionLabel="name"
                  optionValue="id"
                  label={appStore.tr.country || "Country"}
                  placeholder={appStore.tr.select || "Select"}
                />
              </div>

              <div className="col-12 md:col-4">
                <Select
                  options={regions}
                  value={region_id}
                  onChange={(e) => {
                    setRegionId(e)
                  }}
                  optionLabel="name"
                  optionValue="id"
                  label={appStore.tr.region || "Region"}
                  placeholder={appStore.tr.select || "Select"}
                  disabled={!country_id}
                />
              </div>

              <div className="col-12 md:col-4">
                <Select
                  options={districts}
                  value={district_id}
                  onChange={(e) => setDistrictId(e)}
                  optionLabel="name"
                  optionValue="id"
                  label={appStore.tr.district || "District"}
                  placeholder={appStore.tr.select || "Select"}
                  disabled={!region_id}
                />
              </div>

              <div className="col-12">
                <input
                  id="company-search-name"
                  type="text"
                  className="p-inputtext p-component w-full p-inputtext-sm"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={appStore.tr.name || "Name"}
                />
              </div>

              <div className="col-12 flex justify-content-end gap-2">
                <Btn
                  icon="pi pi-times"
                  outlined
                  severity="secondary"
                  label={appStore.tr.clear || "Clear"}
                  onClick={handleClear}
                />

                <Btn
                  icon="pi pi-search"
                  label={appStore.tr.search || "Search"}
                  onClick={handleSearch}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {!companies.length ? (
        <div className="game-card p-4 text-center">
          <div className="text-600">
            {appStore.tr.not_found || "No companies found"}
          </div>
        </div>
      ) : (
        <div className="games-grid">
          {companies.map(renderCompanyCard)}
        </div>
      )}
    </div>
  );
};

export default observer(CompanySearch);